import React, { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Typography from "@mui/material/Typography";
import {
  makeGETRequest,
  makePOSTRequestOnUserService,
  makePUTRequestOnUserService,
} from "../../Api/api";
import toastr from "toastr";
import { StyledTextInput } from "../StyledComponents/StyledInputs";
import { ActionButtons, OrderDetailsInterface } from "./interfaces";
import moment from "moment";
import FormHelperText from "@mui/material/FormHelperText";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Transition from "../Transition";
import { StyledButton } from "./NewRFQOrderDetails";
import generateCashflowPDFBlob from "./generateCashflowPDFBlob";
import { generateCashflow } from "./CreateRFQOrder";
import CashflowPDF from "./CashflowPDF";

export interface IModifyValues {
  value: number;
  sellYield: number;
  sellPrice: number;
  minimumOrderValue: number;
  principalAmount: number;
  modifiedAccruedInterest: number;
  stampDuty: number;
  totalConsiderationWithoutStampDuty: number;
  numberOfBonds: number;
  buyerClientCode: string;
  clientIFSC: string;
  clientBankAccountNumber: string;
  clientDPType: string;
  clientDPID: string;
  clientDPClientID: string;
  settlementDate: string;
  maturityDate: string;
  callDate: string;
  settlementType: string;
}

function isValueString(value: string) {
  return [
    "buyerClientCode",
    "clientIFSC",
    "clientBankAccountNumber",
    "clientDPType",
    "clientDPID",
    "clientDPClientID",
    "settlementDate",
    "maturityDate",
    "callDate",
  ].includes(value);
}

const RFQActionButtons = ({
  orderDetails,
  orderUUID,
  setLoading,
  type,
}: {
  orderDetails: OrderDetailsInterface;
  orderUUID: string;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  type: string;
}) => {
  const [open, setOpen] = useState(false);
  const [internally, setInternally] = useState(false);
  const initialModifyValues = useMemo(
    () => ({
      value: orderDetails?.value!,
      sellYield: orderDetails?.sell_yield!,
      sellPrice: orderDetails?.sell_price!,
      minimumOrderValue: orderDetails?.minimum_order_value!,
      principalAmount: orderDetails?.principal_amount!,
      modifiedAccruedInterest: orderDetails?.modified_accrued_interest!,
      stampDuty: orderDetails?.stamp_duty!,
      totalConsiderationWithoutStampDuty: orderDetails?.total_consideration!,
      numberOfBonds: orderDetails?.no_of_bonds!,
      buyerClientCode: orderDetails?.buyer_client_name!,
      clientIFSC: orderDetails?.initiator_ifsc!,
      clientBankAccountNumber: orderDetails?.initiator_bank_account_number!,
      clientDPType: orderDetails?.initiator_dp_type!,
      clientDPID: orderDetails?.initiator_dpid!,
      clientDPClientID: orderDetails?.initiator_client_id!,
      settlementDate: moment(
        orderDetails?.settlement_date!,
        "MMM DD, YYYY"
      ).format("YYYY-MM-DD"),
      maturityDate: orderDetails?.last_maturity_date
        ? moment(orderDetails?.last_maturity_date, "DD/MM/YYYY").format(
          "YYYY-MM-DD"
        )
        : "",
      callDate: orderDetails?.last_call_date
        ? moment(orderDetails?.last_call_date, "DD/MM/YYYY").format(
          "YYYY-MM-DD"
        )
        : "",
      settlementType: orderDetails?.settlement_type!,
    }),
    [orderDetails]
  );
  const [modifyValues, setModifyValues] = useState(initialModifyValues);

  const [bondDetailsState, setBondDetailsState] = useState<any>(null);
  const [inputValuesState, setInputValuesState] = useState<any>(null);
  const [cashflowRecordsState, setCashflowRecordsState] = useState<any[]>([]);
  const [shouldUploadPDF, setShouldUploadPDF] = useState(false);

  useEffect(() => {
    const uploadPDF = async () => {
      if (!shouldUploadPDF) return;
      try {
        const pdfBlob = await generateCashflowPDFBlob();
        const formData = new FormData();
        formData.append("cashflow_pdf", pdfBlob, "cashflow.pdf");
        formData.append("order_id", orderUUID);
        await makePOSTRequestOnUserService(
          "order_management_v2/cashflow_pdf/upload/",
          formData
        );
        setShouldUploadPDF(false);
      } catch (err) {
        console.error("PDF upload failed", err);
      }
    };
    uploadPDF();
  }, [cashflowRecordsState, shouldUploadPDF, orderUUID]);

  const handleAddRFQ = () => {
    const payload: any = {
      uuid: orderUUID,
      admin_name: JSON.parse(localStorage.getItem("loginUser")!)?.name,
    }
    if (type === "partner") {
      payload.user_type = "partner";
    } else if (type === "client") {
      payload.user_type = "client";
    }
    makePOSTRequestOnUserService("order_management_v2/orders/add-to-rfq/", payload)
      .then((response) => {
        if (response && response.status === 200) {
          toastr.success("Quote Added Successfully!");
          setLoading(true);
        }
      })
      .catch((error) => toastr.error(error));
  };
  const handleAcceptRFQ = () => {
    const payload: any = {
      ModAcrInt: orderDetails?.modified_accrued_interest,
      TotalConsideration: orderDetails?.total_consideration,
      admin_name: JSON.parse(localStorage.getItem("loginUser")!)?.name,
    }
    if (type === "partner") {
      payload.user_type = "partner";
    } else if (type === "client") {
      payload.user_type = "client";
    }
    makePOSTRequestOnUserService(`/order_management_v2/orders/accept_order/${orderUUID}/`, payload)
      .then((response) => {
        if (response && response.status === 200) {
          const { data } = response;
          if (data && data.RFQQuoteAcceptResponseList[0]?.message) {
            toastr.success(data.RFQQuoteAcceptResponseList[0]?.message);
          } else {
            toastr.success("Quote Accepted Successfully!");
          }
          setLoading(true);
        }
      })
      .catch((error) => toastr.error(error));
  };
  const handleModify = () => {
    let apiUrl = "/order_management_v2/orders/";

    if (type === "partner") {
      apiUrl += "?type=partner";
    } else if (type === "client") {
      apiUrl += "?type=client";
    }
    makePUTRequestOnUserService(apiUrl, {
      uuid: orderUUID,
      isin: orderDetails?.isin,
      value: modifyValues.value,
      minimum_order_value: modifyValues.minimumOrderValue,
      sell_yield: modifyValues.sellYield,
      sell_price: modifyValues.sellPrice,
      principal_amount: modifyValues.principalAmount,
      modified_accrued_interest: modifyValues.modifiedAccruedInterest,
      stamp_duty: modifyValues.stampDuty,
      total_consideration: modifyValues.totalConsiderationWithoutStampDuty,
      admin_name: JSON.parse(localStorage.getItem("loginUser")!)?.name,
    })
      .then((response) => {
        if (response && response.status === 200) {
          toastr.success("RFQ Modified Successfully");
          setLoading(true);
          setOpen(false);
        }
      })
      .catch((error) => toastr.error(error));
  };
  const handleModifyInternally = () => {
    const payload: any = {
      uuid: orderUUID,
      isin: orderDetails?.isin,
      value: modifyValues.value,
      minimum_order_value: modifyValues.minimumOrderValue,
      sell_yield: modifyValues.sellYield,
      sell_price: modifyValues.sellPrice,
      principal_amount: modifyValues.principalAmount,
      modified_accrued_interest: modifyValues.modifiedAccruedInterest,
      stamp_duty: modifyValues.stampDuty,
      total_consideration: modifyValues.totalConsiderationWithoutStampDuty,
      buyer_client_name: modifyValues.buyerClientCode,
      admin_name: JSON.parse(localStorage.getItem("loginUser")!)?.name,
      no_of_bonds: modifyValues.numberOfBonds,
      initiator_ifsc: modifyValues.clientIFSC,
      initiator_bank_account_number: modifyValues.clientBankAccountNumber,
      initiator_dp_type: modifyValues.clientDPType,
      initiator_dpid: modifyValues.clientDPID,
      initiator_client_id: modifyValues.clientDPClientID,
      settlement_date: moment(
        modifyValues.settlementDate,
        "YYYY-MM-DD"
      ).format("MMM DD,YYYY"),
      last_maturity_date: modifyValues.maturityDate
        ? moment(modifyValues.maturityDate, "YYYY-MM-DD").format("DD/MM/YYYY")
        : "",
      last_call_date: modifyValues.callDate
        ? moment(modifyValues.callDate, "YYYY-MM-DD").format("DD/MM/YYYY")
        : "",
      settlement_type: modifyValues.settlementType,
    }
    if (type === "partner") {
      payload.user_type = "partner";
    } else if (type === "client") {
      payload.user_type = "client";
    }
    makePUTRequestOnUserService(`/order_management_v2/rfq_modified_internally/`, payload)
      .then((response) => {
        if (response && response.status === 200) {
          toastr.success("RFQ Modified Internally");
          setOpen(false);
          setInternally(false);
          setLoading(true);
        }
      })
      .catch((error) => toastr.error(error));
  };
  const handleModifyInternallyNew = async () => {
    try {
      const originalPrice = orderDetails.sell_price;
      const originalSettlementDate = moment(
        orderDetails.settlement_date,
        "MMM DD,YYYY"
      ).format("YYYY-MM-DD");
      const newPrice = modifyValues.sellPrice;
      const newSettlementDate = moment(
        modifyValues.settlementDate,
        "YYYY-MM-DD"
      ).format("YYYY-MM-DD");
      const shouldRecalc =
        newPrice !== originalPrice ||
        newSettlementDate !== originalSettlementDate;

      const putPayload: any = {
        uuid: orderUUID,
        isin: orderDetails.isin,
        value: modifyValues.value,
        minimum_order_value: modifyValues.minimumOrderValue,
        sell_yield: modifyValues.sellYield,
        sell_price: newPrice,
        principal_amount: modifyValues.principalAmount,
        modified_accrued_interest: modifyValues.modifiedAccruedInterest,
        stamp_duty: modifyValues.stampDuty,
        total_consideration: modifyValues.totalConsiderationWithoutStampDuty,
        buyer_client_name: modifyValues.buyerClientCode,
        admin_name: JSON.parse(localStorage.getItem("loginUser")!)?.name,
        no_of_bonds: modifyValues.numberOfBonds,
        initiator_ifsc: modifyValues.clientIFSC,
        initiator_bank_account_number: modifyValues.clientBankAccountNumber,
        initiator_dp_type: modifyValues.clientDPType,
        initiator_dpid: modifyValues.clientDPID,
        initiator_client_id: modifyValues.clientDPClientID,
        settlement_date: moment(
          modifyValues.settlementDate,
          "YYYY-MM-DD"
        ).format("MMM DD,YYYY"),
        last_maturity_date: modifyValues.maturityDate
          ? moment(modifyValues.maturityDate, "YYYY-MM-DD").format("DD/MM/YYYY")
          : "",
        last_call_date: modifyValues.callDate
          ? moment(modifyValues.callDate, "YYYY-MM-DD").format("DD/MM/YYYY")
          : "",
        settlement_type: modifyValues.settlementType,
      };

      if (shouldRecalc) {
        const bondRes = await makeGETRequest(
          `admin/security_master/get/${orderDetails.isin}/`
        );
        if (bondRes.status !== 200) throw new Error("Failed to fetch bond");

        const rawCashFlow = await generateCashflow(
          bondRes.data,
          newPrice,
          moment(modifyValues.settlementDate, "YYYY-MM-DD")
        );
        if (!rawCashFlow) {
          throw new Error("Cashflow generation failed – got undefined");
        }
        const cashflowRecords = rawCashFlow.map((cf) => ({
          ...cf,
          amount: (
            Number(cf.amount) * Number(modifyValues.numberOfBonds)
          ).toFixed(4),
        }));

        putPayload.cashflow_records = cashflowRecords;

        setBondDetailsState(bondRes.data);
        setInputValuesState({
          count: modifyValues.numberOfBonds,
          price: modifyValues.sellPrice,
          yield: modifyValues.sellYield,
          principle: modifyValues.principalAmount,
          accruedInterest: modifyValues.modifiedAccruedInterest,
          stampDuty: modifyValues.stampDuty,
          totalConsideration: modifyValues.totalConsiderationWithoutStampDuty,
          settlementDate: moment(
            modifyValues.settlementDate,
            "YYYY-MM-DD"
          ).format("DD MMM, YYYY"),
        });
        setCashflowRecordsState(cashflowRecords);
        setShouldUploadPDF(true);
      }
      if (type === "partner") {
        putPayload.user_type = "partner";
      } else if (type === "client") {
        putPayload.user_type = "client";
      }
      const putResp = await makePUTRequestOnUserService(
        `/order_management_v2/rfq_modified_internally/`,
        putPayload
      );
      if (putResp.status !== 200) throw new Error("Modify failed");

      toastr.success("RFQ Modified Internally");
      setOpen(false);
      setInternally(false);
      setLoading(true);
    } catch (err: any) {
      toastr.error(err.message || "Something went wrong");
    }
  };
  const handleCancelRFQ = () => {
    const payload: any = { admin_name: JSON.parse(localStorage.getItem("loginUser")!)?.name }
    if (type === "partner") {
      payload.user_type = "partner";
    } else if (type === "client") {
      payload.user_type = "client";
    }
    makePUTRequestOnUserService(`/order_management_v2/orders/cancel/${orderUUID}/`, payload)
      .then((response) => {
        if (response && response.status === 200) {
          toastr.success("RFQ Cancelled Successfully");
          setLoading(true);
        }
      })
      .catch((error) => toastr.error(error));
  };
  const handleProposeDeal = () => {
    const payload: any = {
      uuid: orderUUID,
      admin_name: JSON.parse(localStorage.getItem("loginUser")!)?.name,
    }
    if (type === "partner") {
      payload.user_type = "partner";
    } else if (type === "client") {
      payload.user_type = "client";
    }
    makePOSTRequestOnUserService(`order_management_v2/rfq_deal_propose/`, payload)
      .then((response) => {
        if (response && response.status === 200) {
          const { data } = response;
          if (data && data.RFQPROPOSEDEALRESPONSE[0]?.message) {
            toastr.success(data.RFQPROPOSEDEALRESPONSE[0]?.message);
          } else {
            toastr.success("DEAL PROPOSED SUCCESSFULLY!");
          }
          setLoading(true);
        }
      })
      .catch((error) => toastr.error(error));
  };
  const handleApproveDeal = () => {
    const payload: any = {
      uuid: orderUUID,
      admin_name: JSON.parse(localStorage.getItem("loginUser")!)?.name,
    }
    if (type === "partner") {
      payload.user_type = "partner";
    } else if (type === "client") {
      payload.user_type = "client";
    }
    makePOSTRequestOnUserService(`order_management_v2/rfq_deal_approve/`, payload)
      .then((response) => {
        if (response && response.status === 200) {
          const { data } = response;
          if (data && data.BROKER_RFQDEALAPPROVERESPONSE[0]?.message) {
            toastr.success(data.BROKER_RFQDEALAPPROVERESPONSE[0]?.message);
          } else {
            toastr.success("TRADE APPROVED AND SENT FOR SETTLEMENT!");
          }
          setLoading(true);
        }
      })
      .catch((error) => toastr.error(error));
  };

  const rfqActionButtons: ActionButtons[] = useMemo(
    () => [
      {
        name: "Add RFQ Quote",
        color: "info",
        icon: <PlaylistAddRoundedIcon />,
        disabled: orderDetails?.is_added_on_rfq as boolean,
        helperText: orderDetails?.is_added_on_rfq
          ? "Quote Added Successfully!"
          : "",
        handleClick: () => handleAddRFQ(),
      },
      {
        name: "Accept RFQ Quote",
        color: "success",
        icon: <DoneAllRoundedIcon />,
        disabled: !orderDetails?.is_added_on_rfq
          ? true
          : orderDetails?.order_status === "Confirmed" ||
            orderDetails?.order_status === "Cancelled"
            ? true
            : false,
        helperText:
          orderDetails?.order_status === "Confirmed" ||
            orderDetails?.order_status === "Cancelled"
            ? `Quote ${orderDetails?.order_status} Successfully!`
            : "",
        handleClick: () => handleAcceptRFQ(),
      },
      {
        name: "Modify Internally",
        color: "warning",
        icon: <EditNoteRoundedIcon />,
        disabled:
          orderDetails?.order_status === "Cancelled" ||
            orderDetails?.order_status === "Confirmed"
            ? true
            : false,
        helperText: "",
        handleClick: () => {
          setModifyValues(initialModifyValues);
          setOpen(true);
          setInternally(true);
        },
      },
      {
        name: "Modify RFQ Quote",
        color: "warning",
        icon: <EditNoteRoundedIcon />,
        disabled: !orderDetails?.is_added_on_rfq
          ? true
          : orderDetails?.order_status === "Cancelled" ||
            orderDetails?.order_status === "Confirmed"
            ? true
            : false,
        helperText: "",
        handleClick: () => {
          setModifyValues(initialModifyValues);
          setOpen(true);
        },
      },
      {
        name: "Cancel RFQ Quote",
        color: "error",
        icon: <DeleteForeverRoundedIcon />,
        disabled: !orderDetails?.is_added_on_rfq
          ? true
          : orderDetails?.order_status === "Cancelled" ||
            orderDetails?.order_status === "Confirmed"
            ? true
            : false,
        helperText:
          orderDetails?.order_status === "Cancelled" ||
            orderDetails?.order_status === "Confirmed"
            ? `RFQ ${orderDetails?.order_status} Successfully`
            : "",
        handleClick: () => handleCancelRFQ(),
      },
      {
        name: "Propose Deal",
        color: "success",
        icon: <DoneAllRoundedIcon />,
        handleClick: () => handleProposeDeal(),
      },
      {
        name: "Approve Deal",
        color: "success",
        icon: <DoneAllRoundedIcon />,
        handleClick: () => handleApproveDeal(),
      },
    ],
    [orderDetails]
  );
  return (
    <>
      <Grid container spacing={2} mt={4}>
        {rfqActionButtons.map((button, index) => (
          <Grid item key={index} xs={6} sm={4} md={2.4}>
            <StyledButton
              startIcon={button.icon}
              color={button.color}
              onClick={button.handleClick}
              disabled={button.disabled}
            >
              {button.name}
            </StyledButton>
            <Typography
              sx={{
                color: "#45E142",
                fontSize: 12,
                textAlign: "center",
                mt: 1,
              }}
            >
              {button.helperText}
            </Typography>
          </Grid>
        ))}
      </Grid>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => {
          setOpen(false);
          internally && setInternally(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Grid container spacing={1}>
            {Object.keys(initialModifyValues).map((item, index) => (
              <Grid item xs={6} key={index}>
                <FormControl fullWidth variant="standard">
                  <InputLabel shrink htmlFor={item}>
                    {item
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())}
                  </InputLabel>
                  <StyledTextInput
                    type={
                      ["settlementDate", "maturityDate", "callDate"].includes(
                        item
                      )
                        ? "date"
                        : isValueString(item)
                          ? "text"
                          : "number"
                    }
                    id={`${item}_${index}`}
                    name={item}
                    value={modifyValues[item as keyof IModifyValues]}
                    {...(item === "settlementType" && {
                      onInput: (e) => {
                        const allowedValues = ["0", "1", "2"];
                        const inputValue = (e.target as HTMLInputElement).value;
                        if (!allowedValues.includes(inputValue)) {
                          (e.target as HTMLInputElement).value = "";
                        }
                      },
                    })}
                    onChange={(event) => {
                      const value = event.target.value;
                      setModifyValues((prev) => ({
                        ...prev,
                        [item as keyof IModifyValues]: isValueString(item)
                          ? value
                          : parseFloat(value),
                      }));
                    }}
                  />
                  {[
                    "totalConsiderationWithoutStampDuty",
                    "settlementDate",
                    "maturityDate",
                    "callDate",
                  ].includes(item) && (
                      <FormHelperText
                        sx={{
                          color: "#45E142",
                          fontSize: 12,
                          ml: 1,
                        }}
                      >
                        {item === "totalConsiderationWithoutStampDuty"
                          ? `With stamp duty ${modifyValues.totalConsiderationWithoutStampDuty +
                          modifyValues.stampDuty
                          }`
                          : "Pattern: DD-MM-YYYY"}
                      </FormHelperText>
                    )}
                </FormControl>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              internally ? handleModifyInternallyNew() : handleModify()
            }
          >
            Modify
          </Button>
        </DialogActions>
      </Dialog>
      {bondDetailsState &&
        inputValuesState &&
        cashflowRecordsState.length > 0 && (
          <CashflowPDF
            bondDetails={bondDetailsState}
            inputvalues={inputValuesState}
            cashflow={cashflowRecordsState}
          />
        )}
    </>
  );
};

export default RFQActionButtons;
